import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import {
    Search,
    Download,
    Calendar,
    Users,
    Filter,
    RefreshCw,
    ChevronLeft,
    ChevronRight,
    UserCheck,
    Network,
    Package,
    TrendingUp,
    Award,
    ArrowUpRight
} from 'lucide-react';
import API_BASE_URL from '../config/api';

const DownlineReport = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState({ total_sales: 0, total_pv: 0, total_members: 0, active_members: 0 });
    const [searchTerm, setSearchTerm] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const fetchReport = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            const url = new URL(`${API_BASE_URL}/user/downline_report.php`);
            if (fromDate) url.searchParams.append('from', fromDate);
            if (toDate) url.searchParams.append('to', toDate);
            if (searchTerm) url.searchParams.append('search', searchTerm);

            const response = await fetch(url.toString(), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            if (result.success) {
                setData(result.data || []);
                setSummary(result.summary || { total_sales: 0, total_pv: 0, total_members: 0, active_members: 0 });
            }
        } catch (error) {
            console.error('Failed to fetch downline report:', error);
        } finally {
            setLoading(false);
        }
    }, [fromDate, toDate, searchTerm]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    const getKycColor = (status) => {
        const s = status?.toLowerCase() || '';
        if (s.includes('approved') || s.includes('verified')) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
        if (s.includes('pending')) return 'bg-amber-100 text-amber-700 border-amber-200';
        if (s.includes('rejected')) return 'bg-red-100 text-red-700 border-red-200';
        return 'bg-slate-100 text-slate-600 border-slate-200';
    };

    // Pagination logic
    const totalPages = Math.ceil(data.length / itemsPerPage);
    const paginatedData = data.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const exportToCSV = () => {
        const headers = ["Distributor ID", "Full Name", "Registered Date", "Expiry Date", "Sponsor", "Parent", "Product", "Placement", "Sales", "PV", "KYC Status"];
        const rows = data.map(item => [
            item.distributor_id,
            item.full_name,
            item.registered_date,
            item.expiry_date,
            item.sponsor,
            item.parent,
            item.product,
            item.placement,
            item.sales,
            item.pv,
            item.kyc_status
        ]);

        let csvContent = "data:text/csv;charset=utf-8,"
            + headers.join(",") + "\n"
            + rows.map(e => e.join(",")).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `Full_Downline_Report_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Layout>
            <div className="min-h-screen bg-slate-50/50 p-4 sm:p-6 lg:p-8">
                {/* Header Section */}
                <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-blue-200">
                                <Users size={24} />
                            </div>
                            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">Downline Master Report</h1>
                        </div>
                        <p className="text-slate-500 font-medium ml-15">All-in-one network analysis, sales, and member tracking</p>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={exportToCSV}
                            className="flex items-center gap-2 px-5 py-3 bg-white border border-slate-200 rounded-2xl text-sm font-bold text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm group"
                        >
                            <Download size={18} className="text-slate-400 group-hover:text-blue-600 transition-colors" />
                            <span>Export All Data</span>
                        </button>
                        <button
                            onClick={fetchReport}
                            className="p-3 bg-blue-600 text-white rounded-2xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 group"
                        >
                            <RefreshCw size={20} className={loading ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'} />
                        </button>
                    </div>
                </div>

                {/* Stat Cards Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm relative overflow-hidden group hover:shadow-xl transition-all">
                        <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50 blur-3xl rounded-full translate-x-1/2 -translate-y-1/2" />
                        <div className="relative z-10">
                            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white mb-4 shadow-lg shadow-blue-100">
                                <TrendingUp size={20} />
                            </div>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Network Sales</p>
                            <h3 className="text-2xl font-black text-slate-900">{formatCurrency(summary.total_sales)}</h3>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm relative overflow-hidden group hover:shadow-xl transition-all">
                        <div className="absolute top-0 right-0 w-24 h-24 bg-purple-50 blur-3xl rounded-full translate-x-1/2 -translate-y-1/2" />
                        <div className="relative z-10">
                            <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center text-white mb-4 shadow-lg shadow-purple-100">
                                <Award size={20} />
                            </div>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Network PV</p>
                            <h3 className="text-2xl font-black text-slate-900">{summary.total_pv.toLocaleString()} <span className="text-xs font-bold text-slate-400">PV</span></h3>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm relative overflow-hidden group hover:shadow-xl transition-all">
                        <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-50 blur-3xl rounded-full translate-x-1/2 -translate-y-1/2" />
                        <div className="relative z-10">
                            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center text-white mb-4 shadow-lg shadow-indigo-100">
                                <Users size={20} />
                            </div>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Downlines</p>
                            <h3 className="text-2xl font-black text-slate-900">{summary.total_members} <span className="text-xs font-bold text-slate-400">Users</span></h3>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm relative overflow-hidden group hover:shadow-xl transition-all">
                        <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-50 blur-3xl rounded-full translate-x-1/2 -translate-y-1/2" />
                        <div className="relative z-10">
                            <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center text-white mb-4 shadow-lg shadow-emerald-100">
                                <UserCheck size={20} />
                            </div>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Active Members</p>
                            <h3 className="text-2xl font-black text-slate-900">{summary.active_members} <span className="text-xs font-bold text-slate-400">Active</span></h3>
                        </div>
                    </div>
                </div>

                {/* Filters Row */}
                <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-xl shadow-slate-200/50 mb-8">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Search Anything</label>
                            <div className="relative group">
                                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600 transition-colors" />
                                <input
                                    type="text"
                                    placeholder="Name, ID or Email..."
                                    value={searchTerm}
                                    onChange={handleSearch}
                                    className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border border-transparent rounded-2xl focus:bg-white focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all font-bold text-sm"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Joined From</label>
                            <div className="relative group cursor-pointer" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                                <Calendar size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-hover:text-blue-600 transition-colors" />
                                <input
                                    type="date"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border border-transparent rounded-2xl focus:bg-white focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all font-bold text-sm cursor-pointer"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Joined To</label>
                            <div className="relative group cursor-pointer" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                                <Calendar size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-hover:text-blue-600 transition-colors" />
                                <input
                                    type="date"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border border-transparent rounded-2xl focus:bg-white focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all font-bold text-sm cursor-pointer"
                                />
                            </div>
                        </div>

                        <div className="flex items-end">
                            <button
                                onClick={fetchReport}
                                className="w-full py-3.5 bg-slate-900 text-white rounded-2xl font-black uppercase tracking-widest text-[11px] hover:bg-black transition-all shadow-lg hover:shadow-slate-300 flex items-center justify-center gap-2"
                            >
                                <Filter size={16} />
                                Refresh Report
                            </button>
                        </div>
                    </div>
                </div>

                {/* Table Container */}
                <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-2xl shadow-slate-200/60 overflow-hidden">
                    <>
                        {/* Desktop View */}
                        <div className="hidden lg:block overflow-x-auto">
                            <table className="w-full border-collapse">
                                <thead>
                                    <tr className="bg-slate-50/50 border-b border-slate-100">
                                        <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Sr.</th>
                                        <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Distributor Details</th>
                                        <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Network Info</th>
                                        <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Contribution (Sales/PV)</th>
                                        <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Product & Placement</th>
                                        <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Registration</th>
                                        <th className="px-6 py-5 text-center text-[10px] font-black text-slate-400 uppercase tracking-widest">KYC Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {loading ? (
                                        <tr>
                                            <td colSpan="7" className="py-24 text-center">
                                                <div className="flex flex-col items-center gap-4">
                                                    <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                                    <p className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] animate-pulse">Syncing All Network Data...</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : paginatedData.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="py-24 text-center">
                                                <div className="flex flex-col items-center gap-4 opacity-30">
                                                    <Users size={64} className="text-slate-300" />
                                                    <p className="text-sm font-black text-slate-400 uppercase tracking-widest">No Members Found</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        paginatedData.map((item, index) => (
                                            <tr key={item.distributor_id} className="hover:bg-slate-50/70 transition-colors group">
                                                <td className="px-6 py-6">
                                                    <span className="text-xs font-black text-slate-300">#{((currentPage - 1) * itemsPerPage) + index + 1}</span>
                                                </td>
                                                <td className="px-6 py-6">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-black text-sm shadow-lg shadow-blue-100 group-hover:scale-110 transition-transform">
                                                            {item.full_name?.charAt(0)}
                                                        </div>
                                                        <div>
                                                            <p className="font-black text-slate-800 text-sm group-hover:text-blue-600 transition-colors">{item.full_name}</p>
                                                            <div className="flex items-center gap-1.5 mt-0.5">
                                                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-tighter bg-slate-100 px-1.5 py-0.5 rounded">ID: {item.distributor_id}</span>
                                                                <span className="text-[9px] font-bold text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded-full border border-blue-100">LVL {item.level}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-6 font-bold">
                                                    <div className="space-y-1.5">
                                                        <div className="flex items-center gap-2">
                                                            <UserCheck size={14} className="text-slate-400" />
                                                            <span className="text-[10px] text-slate-500 uppercase">Sponsor:</span>
                                                            <span className="text-xs text-slate-700 font-black">{item.sponsor}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <Network size={14} className="text-slate-400" />
                                                            <span className="text-[10px] text-slate-500 uppercase">Parent:</span>
                                                            <span className="text-xs text-slate-700 font-black">{item.parent}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-6">
                                                    <div className="space-y-1">
                                                        <p className="font-black text-slate-900 text-sm">{formatCurrency(item.sales)}</p>
                                                        <div className="flex items-center gap-1 text-[10px] font-black text-emerald-600 uppercase tracking-tighter">
                                                            <span>{item.pv.toLocaleString()} PV</span>
                                                            <ArrowUpRight size={10} />
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-6">
                                                    <div className="space-y-2">
                                                        <div className="flex items-center gap-2">
                                                            <Package size={14} className="text-emerald-500" />
                                                            <span className="text-xs font-black text-slate-800">{item.product}</span>
                                                        </div>
                                                        <span className={`inline-block px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter ${item.placement === 'Left' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                                                            }`}>
                                                            {item.placement} Position
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-6">
                                                    <div className="p-1 px-2 border border-blue-200 bg-blue-50/50 rounded-lg inline-block">
                                                        <p className="text-[8px] font-black text-blue-400 uppercase tracking-widest leading-none mb-1">Joined</p>
                                                        <p className="text-[11px] font-black text-blue-700 leading-none">{formatDate(item.registered_date)}</p>
                                                        <p className="text-[9px] font-bold text-blue-400 mt-1">{formatTime(item.registered_date)}</p>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-6 text-center">
                                                    <span className={`px-4 py-1.5 rounded-2xl text-[10px] font-black uppercase border tracking-widest shadow-sm ${getKycColor(item.kyc_status)}`}>
                                                        {item.kyc_status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile View */}
                        <div className="lg:hidden p-4 space-y-4">
                            {loading ? (
                                <div className="py-12 flex flex-col items-center justify-center">
                                    <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                    <p className="mt-4 text-xs font-bold text-slate-400 tracking-wider">Syncing Data...</p>
                                </div>
                            ) : paginatedData.length === 0 ? (
                                <div className="py-12 text-center">
                                    <Users size={48} className="mx-auto text-slate-300 mb-3" />
                                    <p className="text-sm font-bold text-slate-500">No Members Found</p>
                                </div>
                            ) : paginatedData.map((item, index) => (
                                <div key={item.distributor_id} className="bg-white border border-slate-100 p-4 rounded-2xl shadow-sm flex flex-col gap-3">
                                    <div className="flex justify-between items-start">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white font-black text-sm shadow-md">
                                                {item.full_name?.charAt(0)}
                                            </div>
                                            <div>
                                                <p className="font-bold text-slate-800 text-sm line-clamp-1">{item.full_name}</p>
                                                <div className="flex items-center gap-1.5 mt-0.5">
                                                    <span className="text-[10px] font-bold text-slate-500 uppercase">ID: {item.distributor_id}</span>
                                                    <span className="text-[10px] font-black text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full border border-blue-100">LVL {item.level}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <span className={`px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-widest border ${getKycColor(item.kyc_status)}`}>
                                            {item.kyc_status}
                                        </span>
                                    </div>

                                    <div className="bg-slate-50 rounded-xl p-3 border border-slate-100 space-y-2">
                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                            <div>
                                                <span className="text-[9px] font-black text-slate-400 uppercase block mb-0.5">Sponsor</span>
                                                <span className="font-bold text-slate-700">{item.sponsor}</span>
                                            </div>
                                            <div>
                                                <span className="text-[9px] font-black text-slate-400 uppercase block mb-0.5">Parent</span>
                                                <span className="font-bold text-slate-700">{item.parent}</span>
                                            </div>
                                            <div>
                                                <span className="text-[9px] font-black text-slate-400 uppercase block mb-0.5">Sales</span>
                                                <span className="font-bold text-slate-900">{formatCurrency(item.sales)}</span>
                                            </div>
                                            <div>
                                                <span className="text-[9px] font-black text-slate-400 uppercase block mb-0.5">PV</span>
                                                <span className="font-black text-emerald-600">{item.pv.toLocaleString()}</span>
                                            </div>
                                            <div className="col-span-2 flex justify-between items-center pt-2 border-t border-slate-200 mt-1">
                                                <span className="text-[10px] font-bold text-slate-500">{item.product}</span>
                                                <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${item.placement === 'Left' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                                                    {item.placement} Pos
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between mt-1">
                                        <div className="flex items-center gap-1.5 text-slate-400">
                                            <Calendar size={12} />
                                            <span className="text-[10px] font-bold">{formatDate(item.registered_date)}</span>
                                        </div>
                                        <span className="text-[10px] font-black text-slate-300">#{((currentPage - 1) * itemsPerPage) + index + 1}</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="p-6 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between">
                                <p className="text-xs font-bold text-slate-400">
                                    Showing <span className="text-slate-900">{((currentPage - 1) * itemsPerPage) + 1}</span> to <span className="text-slate-900">{Math.min(currentPage * itemsPerPage, data.length)}</span> of <span className="text-slate-900">{data.length}</span> members
                                </p>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        className="p-2 bg-white border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-all shadow-sm"
                                    >
                                        <ChevronLeft size={20} />
                                    </button>
                                    <div className="flex gap-1 px-4">
                                        {[...Array(totalPages)].map((_, i) => (
                                            <button
                                                key={i}
                                                onClick={() => setCurrentPage(i + 1)}
                                                className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${currentPage === i + 1
                                                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
                                                    : 'text-slate-400 hover:bg-white hover:text-slate-600'
                                                    }`}
                                            >
                                                {i + 1}
                                            </button>
                                        ))}
                                    </div>
                                    <button
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                        className="p-2 bg-white border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-all shadow-sm"
                                    >
                                        <ChevronRight size={20} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                </div>
            </div>

            <style>{`
                .tracking-tighter { letter-spacing: -0.02em; }
                input[type="date"]::-webkit-calendar-picker-indicator {
                    opacity: 0.1;
                }
            `}</style>
        </Layout>
    );
};

export default DownlineReport;
